import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaMagic, FaSpinner, FaDownload, FaEdit, FaCheck } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PreviewParsed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [cvData, setCvData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('bauhaus-modern');
  const [templates, setTemplates] = useState([]);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    loadTemplates();
    generateCV();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.filter(t => !t.is_premium));
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const generateCV = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { prompt, jobDescription } = location.state || {};
      
      if (!prompt) {
        setError('Aucun prompt fourni');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/generate-cv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          prompt,
          job_description: jobDescription,
          template_id: selectedTemplate
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la génération');
      }

      const data = await response.json();
      setCvData(data.cv_data);
      setIsDemo(data.is_demo);
      setIsLoading(false);

    } catch (error) {
      console.error('CV generation error:', error);
      setError(error.message);
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!cvData) return;
    
    try {
      setIsPDFLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          cv_data: cvData,
          template_id: selectedTemplate
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      // Download PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CV_${cvData.first_name}_${cvData.last_name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setIsPDFLoading(false);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
      setIsPDFLoading(false);
    }
  };

  const handleEdit = () => {
    navigate('/create', { state: { cvData } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a1d] text-gray-200 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-16 h-16 text-[#00d9ff] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Génération de votre CV...</h2>
          <p className="text-gray-400">L'IA analyse votre parcours</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#1a1a1d] text-gray-200 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-red-900/20 border border-red-500 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Erreur</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="px-6 py-3 bg-[#00d9ff] hover:bg-[#00c4ea] rounded-lg transition-colors text-black font-medium">
            Retour au Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!cvData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#1a1a1d] text-gray-200">
      {/* Header */}
      <nav className="border-b border-gray-800 bg-[#242428]/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaMagic className="w-5 h-5 sm:w-6 sm:h-6 text-[#00d9ff]" />
            <span className="text-lg sm:text-xl font-bold">
              SkillForge <span className="text-[#00d9ff]">Pro</span>
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {isDemo && (
              <span className="px-2 sm:px-3 py-1 bg-yellow-900/30 border border-yellow-600 rounded-full text-xs text-yellow-500">
                Démo
              </span>
            )}
            <button onClick={handleEdit} className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-sm">
              <FaEdit className="w-4 h-4" />
              Éditer
            </button>
            <button onClick={handleDownloadPDF} disabled={isPDFLoading} className="flex items-center gap-2 px-3 sm:px-6 py-2 bg-[#00d9ff] hover:bg-[#00c4ea] rounded-lg transition-colors font-medium text-black text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              {isPDFLoading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Génération...</span>
                </>
              ) : (
                <>
                  <FaDownload className="w-4 h-4" />
                  <span className="hidden sm:inline">Télécharger</span> PDF
                </>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Templates Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[#242428] border border-gray-800 rounded-xl p-4">
              <h3 className="text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
                <span>Templates</span>
                <span className="text-xs text-gray-500">({templates.length})</span>
              </h3>
              <div className="space-y-2">
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedTemplate === template.id
                        ? 'bg-[#00d9ff] text-black'
                        : 'bg-gray-800/50 hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs opacity-75 capitalize">{template.style}</div>
                      </div>
                      {selectedTemplate === template.id && (
                        <FaCheck className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CV Preview */}
          <div className="lg:col-span-3">
            <div className="bg-white text-gray-900 rounded-xl shadow-2xl p-6 sm:p-8">
              {/* Header */}
              <div className="border-b-2 border-[#00d9ff] pb-6 mb-6">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {cvData.first_name} {cvData.last_name}
                </h1>
                <p className="text-lg sm:text-xl text-[#00d9ff] mt-2 font-medium">{cvData.title}</p>
                <div className="flex flex-wrap gap-3 sm:gap-4 mt-4 text-xs sm:text-sm text-gray-600">
                  {cvData.email && <span>📧 {cvData.email}</span>}
                  {cvData.phone && <span>📱 {cvData.phone}</span>}
                  {cvData.location && <span>📍 {cvData.location}</span>}
                  {cvData.linkedin && <span>🔗 LinkedIn</span>}
                </div>
              </div>

              {/* Summary */}
              {cvData.summary && (
                <div className="mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 border-l-4 border-[#00d9ff] pl-3">
                    Profil
                  </h2>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{cvData.summary}</p>
                </div>
              )}

              {/* Experience */}
              {cvData.experiences && cvData.experiences.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 border-l-4 border-[#00d9ff] pl-3">
                    Expérience Professionnelle
                  </h2>
                  {cvData.experiences.map((exp, idx) => (
                    <div key={idx} className="mb-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                        <div>
                          <h3 className="text-base sm:text-lg font-bold text-gray-900">{exp.position}</h3>
                          <p className="text-[#00d9ff] font-medium text-sm sm:text-base">{exp.company}</p>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-0">
                          {exp.start_date} - {exp.end_date}
                        </span>
                      </div>
                      {exp.achievements && exp.achievements.length > 0 && (
                        <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1">
                          {exp.achievements.map((achievement, i) => (
                            <li key={i}>{achievement}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {cvData.education && cvData.education.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 border-l-4 border-[#00d9ff] pl-3">
                    Formation
                  </h2>
                  {cvData.education.map((edu, idx) => (
                    <div key={idx} className="mb-3">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base">{edu.degree}</h3>
                      <p className="text-[#00d9ff] text-sm sm:text-base">{edu.school}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{edu.year}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Skills */}
              {cvData.skills && cvData.skills.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 border-l-4 border-[#00d9ff] pl-3">
                    Compétences
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {cvData.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#00d9ff]/10 text-[#00d9ff] border border-[#00d9ff]/30 rounded-full text-xs sm:text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewParsed;
